import { Injectable } from '@angular/core';
import { Observable, Subject, pipe, concat, of, EMPTY } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { CacheService, CacheStoreOptions, IndexedDBCacheService, Preferences } from '@library/core';
import { HttpPagination, ResponseWrapper } from '@library/common/http';
import { Filter } from '@library/modules/sidebar';
import { IQuotation, QuotationSnapshot } from '@library/modules/quotation';

@Injectable({
  providedIn: 'root'
})
export class QuotationSnapshotService {

  url = 'quotations/list';

  public refresh$: Subject<void> = new Subject();
  public reset$: Subject<void> = new Subject();
  public destroy$: Subject<boolean> = new Subject();

  public filters: Filter<QuotationSnapshot>[] = [];

  private readonly cacheExpiryDays = 92;

  public sort_types = [
    {
      field: 'fwq',
      name: 'Request',
    },
    {
      field: 'createdDate',
      name: 'Create Date',
    },
    {
      field: 'validToDate',
      name: 'Validity Date',
    },
    {
      field: 'serviceLevel',
      name: 'Service Level',
    },
    {
      field: 'cost',
      name: 'Cost',
    },
  ];

  public get sort_type() {
    return this.prefs.getPref('quoteSnap.sort_type');
  }

  public get sort_asc() { return this.prefs.getPref( 'quoteSnap.sort_asc' ) || false; }
  
  public set sort_asc( value ) {
    this.prefs.upsertPref( 'quoteSnap.sort_asc', value );
    this.refresh$.next();
  }

  public set sort_type( value ) {
    this.prefs.upsertPref( 'quoteSnap.sort_type', value );
    this.refresh$.next();
  }

  constructor (
    private prefs: Preferences,
    private cacheService: CacheService,
    // private reqData: CacheRequestService,
    private pagination: HttpPagination,
    private idb: IndexedDBCacheService,
  ) { }

  private cacheTag( accountId: number ): string {
    return `quotation-account-${accountId}`;
  }

  private cacheKey( accountId: number, fwq?: string ): string {
    return `quotation:${accountId}:${fwq}`;
  }

  private expiresAt( item: Partial<IQuotation> ): number | undefined {
    if ( !item?.createdDate ) { return undefined; }
    const created = new Date( `${item.createdDate}T${item.createdTime || '00:00:00'}.000Z` ).getTime();
    return isNaN( created ) ? undefined : created + this.cacheExpiryDays * 24 * 60 * 60 * 1000;
  }

  private isExcluded( item: Partial<IQuotation> ): boolean {
    const expiresAt = this.expiresAt( item );
    return item?.cancelled === true || ( !!expiresAt && expiresAt < Date.now() );
  }

  private toSnapshots( list: IQuotation[] ): QuotationSnapshot[] {
    return list.map( i => new QuotationSnapshot( i ));
  }

  private getAllFromDB( accountId: number ): Observable<IQuotation[]> {
    return this.idb.getByTag<IQuotation>( this.cacheTag( accountId ) ).pipe(
      map( entries => entries.map( entry => entry.data ).filter( data => !this.isExcluded( data ))),
    );
  }

  private saveItemsToDB( items: IQuotation[], accountId: number ): Observable<void> {
    return this.idb.setMany( items.map( item => {
      const options: CacheStoreOptions = { tags: [ this.cacheTag( accountId ) ] };
      const expiresAt = this.expiresAt( item );
      if ( expiresAt ) { options.expiresAt = expiresAt; }

      return {
        key: this.cacheKey( accountId, item.fwq ),
        data: JSON.parse( JSON.stringify( item )),
        options,
      };
    }));
  }

  invalidateOldEntries(): Observable<void> {
    return this.idb.clearExpired().pipe(
      switchMap( () => this.idb.getAll<IQuotation>() ),
      map( entries => entries
        .filter( entry => entry.key.startsWith( 'quotation:' ) && this.isExcluded( entry.data ))
        .map( entry => entry.key )),
      switchMap( keys => this.idb.invalidate({ keys }) ),
      map( () => undefined ),
    );
  }

  list$( activeAccountId: number ): Observable<QuotationSnapshot[]> {
    const network$ = this.cacheService.request<ResponseWrapper<IQuotation>, IQuotation[]>({
      url: this.url,
      params: {},
      refresher: this.refresh$,
      pipe: pipe(
        map( rw => rw.response.collection ),
      ),
      useLambda: true,
    })
    .pipe(
      switchMap( incoming => this.getAllFromDB( activeAccountId ).pipe(
        switchMap( stored => {
          if ( !incoming.length ) { return of( [] as QuotationSnapshot[] ); }
          const excludedFwqs = incoming.filter( item => this.isExcluded( item )).map( item => item.fwq );
          const staleKeys = stored
            .filter( s => excludedFwqs.includes( s.fwq ))
            .map( s => this.cacheKey( activeAccountId, s.fwq ));
          const changed = incoming.filter( item => {
            if ( this.isExcluded( item )) { return false; }
            const match = stored.find( s => s.fwq === item.fwq );
            return !match || JSON.stringify( match ) !== JSON.stringify( item );
          });
          if ( !changed.length && !staleKeys.length ) { return stored.length ? EMPTY : of( [] as QuotationSnapshot[] ); }
          return this.idb.invalidate({ keys: staleKeys }).pipe(
            switchMap( () => this.saveItemsToDB( changed, activeAccountId )),
            switchMap( () => this.getAllFromDB( activeAccountId )),
            map( all => this.toSnapshots( all )),
          );
        }),
      )),
    );

    return this.getAllFromDB( activeAccountId ).pipe(
      switchMap( stored => stored.length ? concat( of( this.toSnapshots( stored )), network$ ) : network$ ),
    );
  }

  sort( list: QuotationSnapshot[] ): QuotationSnapshot[] {
    if ( !list.length ) { return list; }

    let sorted = [];

    switch ( this.sort_type ) {
        case 'fwq':
          sorted = list.sort( ( a, b ) => +b.fwq - +a.fwq );
          break;
        case 'serviceLevel':
          sorted = list.sort( ( a, b ) => b.serviceLevel.localeCompare( a.serviceLevel ));
          break;
        case 'cost':
          sorted = list.sort(( a, b ) => (+b.cost - +a.cost));
          break;
        case 'validToDate':
          sorted = list.sort((a, b) => {
            const dateA = a.validToDate ? new Date(a.validToDate).getTime() : 0; // Assign 0 for empty dates
            const dateB = b.validToDate ? new Date(b.validToDate).getTime() : 0;
            return dateB - dateA; // Descending order
          });
          break;
        case 'createdDate':
        default:
          sorted = list.sort( ( a, b ) => (b.datetime - a.datetime) );
    }

    return this.sort_asc ? sorted.reverse() : sorted;
  }

  stop() {
    console.warn( 'Defunct stop function called' );
  }

  start() {
    this.reset();
  }

  reset(): void {
    this.filters = [];
    this.reset$.next();
    this.cacheService.cancelPagination( this.pagination.hash );
    this.pagination.reset();
 }

}
